import { useEffect, useMemo, useState } from 'react';

import { ControlTowerData, ControlTowerQuery, CutoffTimesData, TollGateData, useApi } from './api';

export const guarantee100Procent = (input: TollGateData) => {
  const relevantKeys = [
    'beforeThreshold',
    'afterThreshold',
    'attemptBeforeThreshold',
    'attemptAfterThreshold',
    'restStillTime',
    'restTooLate',
  ];
  const total = relevantKeys.reduce((acc, k) => (input[k] || 0) + acc, 0);
  if (total > 100) {
    const diff = total - 100;
    // reduce largest part
    const largestKey = relevantKeys.reduce((a, k) => (input[k] > input[a] ? k : a));
    input[largestKey] = input[largestKey] - diff;
  }
  return input;
};

const capTo100Percent = (input: ControlTowerData): ControlTowerData => {
  return Object.keys(input).reduce(
    (acc, k) => ({
      ...acc,
      [k]: input[k].map(guarantee100Procent),
    }),
    {} as ControlTowerData,
  );
};

export const transformToPercentages = (data: ControlTowerData) => {
  const res = Object.keys(data).reduce((acc, day: keyof ControlTowerData) => {
    // total number of orders for this day
    const total = Object.values(data[day].find((c) => c.threshold === 'sorted')).reduce(
      (a, c) => (typeof c === 'number' ? a + c : a),
      0,
    );
    return {
      ...acc,
      [day]: data[day].map((t) =>
        Object.keys(t).reduce(
          (at, td) => ({
            ...at,
            [td]: typeof t[td] === 'number' && total > 0 ? Math.round((t[td] / total) * 100) : t[td],
          }),
          {} as TollGateData,
        ),
      ),
    };
  }, {} as ControlTowerData);
  console.log(res);
  return capTo100Percent(res);
};

export const useControlTower = () => {
  const { getControlTowerData } = useApi();
  const [relativeData, setRelativeData] = useState<ControlTowerData>(null);
  const [absoluteData, setAbsoluteData] = useState<ControlTowerData>(null);

  const [query, setQuery] = useState<ControlTowerQuery>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [units, setUnits] = useState<'orders' | 'percentages'>('percentages');
  const refresh = () => fetchIt({ ...query, refresh: true });

  const fetchIt = async (query?: ControlTowerQuery) => {
    setLoading(true);
    setError(null);

    const { error, data } = await getControlTowerData(query);
    setError(error);

    setAbsoluteData(data);
    setRelativeData(transformToPercentages(data));
    setLoading(false);
  };

  const data = useMemo(
    () => (units === 'percentages' ? relativeData : absoluteData),
    [units, relativeData, absoluteData],
  );

  useEffect(() => {
    fetchIt(query);
  }, [query]);

  return { loading, error, data, setQuery, query, units, setUnits, refresh };
};

export const useCutoffTimes = () => {
  const { getCutoffValues } = useApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allCutoffData, setAllCutoffData] = useState<CutoffTimesData>(null);

  const [search, setSearch] = useState('');

  const fetchIt = async (query?: ControlTowerQuery) => {
    setLoading(true);
    setError(null);

    const { error, data } = await getCutoffValues(query);
    setError(error);

    setAllCutoffData(data);
    setLoading(false);
  };
  const cutoffData = useMemo(() => {
    const terms = search.trim().toLowerCase().split(' ');

    return allCutoffData?.filter((d) =>
      terms.every((t) => Object.values(d).some((v) => typeof v === 'string' && v.trim().toLowerCase().includes(t))),
    );
  }, [search, allCutoffData]);
  return { error, loading, cutoffData, fetchIt, setSearch };
};
